import { c as _c } from "react/compiler-runtime";
import type { ComparisonTableColumn, ComparisonTableColumnDriver } from '@cc-shopping/contracts/credit-cards-page/reviews/schema/types';
import { InfoTooltip } from '@cc-shopping/ui/composites/InfoTooltip';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import DOMPurify from 'isomorphic-dompurify';
import { useMemo } from 'react';
import { RatesAndFeesLink } from 'src/features/product-card/RatesAndFeesLink';
import { MAXIMUM_COLUMNS_MOBILE } from '../Context';
type ComparisonTableBodyProps = {
  column: ComparisonTableColumn;
  columnIndex: number;
  driver: ComparisonTableColumnDriver;
  isLastDriver: boolean;
};
export default function ComparisonTableRow(t0) {
  const $ = _c(45);
  const {
    column,
    columnIndex,
    driver,
    isLastDriver
  } = t0;
  const {
    isPrimaryEntity,
    marketplaceEntity: t1
  } = column;
  const {
    ctaAnalytics,
    termsAndFeesLink,
    versionId,
    signUpBonusDetailsAnnotation,
    name,
    displayName
  } = t1;
  const {
    label,
    value,
    valueTooltip,
    valueTooltipHasHtml,
    description
  } = driver;
  let t2;
  if ($[0] !== valueTooltip || $[1] !== valueTooltipHasHtml) {
    t2 = valueTooltip && valueTooltipHasHtml ? DOMPurify.sanitize(valueTooltip) : valueTooltip;
    $[0] = valueTooltip;
    $[1] = valueTooltipHasHtml;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const sanitizedTooltip = t2;
  const t3 = `driver-container-${columnIndex}-${driver.id}`;
  const t4 = isPrimaryEntity ? 5 : 0;
  const t5 = isPrimaryEntity ? "2" : "1";
  const t6 = columnIndex > MAXIMUM_COLUMNS_MOBILE - 1 ? "none" : "flex";
  let t7;
  if ($[3] !== t6) {
    t7 = {
      md: "flex",
      xs: t6
    };
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const t8 = columnIndex === 0 ? "1px solid" : 0;
  let t9;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      xs: 3.5,
      sm: 1
    };
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  let t10;
  if ($[6] !== t8) {
    t10 = {
      borderBottom: "1px solid",
      borderRight: "1px solid",
      borderLeft: t8,
      borderColor: "neutral.light",
      backgroundColor: "neutral.lightest",
      paddingY: 1,
      paddingX: t9
    };
    $[6] = t8;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== label) {
    t11 = label.toUpperCase();
    $[8] = label;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== t11) {
    t12 = <Typography variant="textSmallBold" lineHeight={1.5} fontWeight="bold" marginTop={1} letterSpacing=".0625em">{t11}</Typography>;
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      wordBreak: "break-word"
    };
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const t14 = value || "N/A";
  let t15;
  if ($[13] !== t14) {
    t15 = <Typography variant="text" lineHeight={1.5} sx={t13}>{t14}</Typography>;
    $[13] = t14;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] !== label || $[16] !== sanitizedTooltip || $[17] !== valueTooltipHasHtml) {
    t16 = sanitizedTooltip && <Box sx={{
      position: "absolute",
      display: "inline-block",
      bottom: "-2px"
    }}><InfoTooltip ariaLabel={`More information about ${label}`}>{valueTooltipHasHtml ? <Box sx={{
          "& td": {
            border: "none",
            fontSize: 13,
            lineHeight: 1.5
          }
        }} dangerouslySetInnerHTML={{
          __html: sanitizedTooltip
        }} /> : <Typography variant="textSmall">{sanitizedTooltip}</Typography>}</InfoTooltip></Box>;
    $[15] = label;
    $[16] = sanitizedTooltip;
    $[17] = valueTooltipHasHtml;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== t15 || $[20] !== t16) {
    t17 = <Box position="relative" alignSelf="center" marginTop={1} textAlign="center">{t15}{t16}</Box>;
    $[19] = t15;
    $[20] = t16;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== description || $[23] !== label || $[24] !== signUpBonusDetailsAnnotation) {
    t18 = label.toLowerCase() === "intro offer" && signUpBonusDetailsAnnotation ? null : description && <Typography variant="textSmall" textAlign="center" sx={{
      display: "block",
      color: "grey.500"
    }}>{description}</Typography>;
    $[22] = description;
    $[23] = label;
    $[24] = signUpBonusDetailsAnnotation;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== ctaAnalytics || $[27] !== displayName || $[28] !== isLastDriver || $[29] !== name || $[30] !== termsAndFeesLink || $[31] !== versionId) {
    t19 = isLastDriver && termsAndFeesLink && <Box display="flex" justifyContent="center"><RatesAndFeesLink analytics={{
        ...ctaAnalytics,
        linkType: "RATES_AND_FEES_TEXT"
      }} href={termsAndFeesLink} versionId={versionId} additionalParams={{
        monetizable: ctaAnalytics.monetizable
      }} showBlankTargetIcon={true} name={displayName || name} /></Box>;
    $[26] = ctaAnalytics;
    $[27] = displayName;
    $[28] = isLastDriver;
    $[29] = name;
    $[30] = termsAndFeesLink;
    $[31] = versionId;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t17 || $[34] !== t18 || $[35] !== t19) {
    t20 = <Box display="flex" flexDirection="column">{t17}{t18}{t19}</Box>;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t10 || $[38] !== t12 || $[39] !== t20 || $[40] !== t3 || $[41] !== t4 || $[42] !== t5 || $[43] !== t7) {
    t21 = <Grid data-testid={t3} boxShadow={t4} zIndex={t5} display={t7} flexDirection="column" justifyContent="flex-start" alignItems="center" sx={t10} gap={1} flexGrow={1} paddingBottom={2} size={12}>{t12}{t20}</Grid>;
    $[37] = t10;
    $[38] = t12;
    $[39] = t20;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t7;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  return t21;
}